
import React, { useEffect, useState, memo } from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { HardDrive } from 'lucide-react';
import { getStorageUsage } from '@/utils/deployment/storageQuotaManager';
import { calculateSystemHealth, HealthStatus } from '@/utils/health/healthCalculator';

export interface StorageHealthIndicatorProps {
  /**
   * Show detailed storage information
   */
  showDetails?: boolean;
  
  /**
   * Options for customizing the storage indicator
   */
  options?: {
    showToasts?: boolean;
  };
}

// Add quota to storage data type
interface StorageData {
  usage: number;
  percentage: number;
  remaining: number;
  quota?: number; // Make quota optional
}

/**
 * StorageHealthIndicator - Component for displaying storage health
 * Shows storage usage and health status
 */
const StorageHealthIndicator: React.FC<StorageHealthIndicatorProps> = memo(({
  showDetails = false,
  options = {}
}) => {
  const [storageData, setStorageData] = useState<StorageData | null>(null);
  const [healthStatus, setHealthStatus] = useState<HealthStatus>(HealthStatus.GOOD);
  
  useEffect(() => {
    try {
      // Get current storage usage
      const storage = getStorageUsage();
      
      // Calculate health status
      const health = calculateSystemHealth({
        storagePercentage: storage.percentage * 100
      });
      
      // Add quota to storage data
      const enhancedStorageData: StorageData = {
        ...storage,
        quota: storage.remaining + storage.usage
      };
      
      setStorageData(enhancedStorageData);
      setHealthStatus(health.status);
    } catch (error) {
      console.error('Error getting storage data:', error);
    }
  }, []);
  
  // Format bytes to human-readable size
  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Get color based on health status
  const getStatusColor = (): string => {
    switch (healthStatus) {
      case HealthStatus.WARNING:
        return 'text-amber-500';
      case HealthStatus.CRITICAL:
        return 'text-red-500';
      case HealthStatus.GOOD:
      default:
        return 'text-green-500';
    }
  };
  
  if (!storageData) {
    return null;
  }
  
  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <HardDrive className={`h-4 w-4 ${getStatusColor()}`} />
          <span className="text-sm font-medium">Storage</span>
        </div>
        <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
          {Math.round(storageData.percentage * 100)}%
        </span>
      </div>
      
      <Progress 
        value={storageData.percentage * 100}
        className="h-1.5 mb-2"
        indicatorClassName={
          healthStatus === HealthStatus.GOOD ? 'bg-green-500' :
          healthStatus === HealthStatus.WARNING ? 'bg-amber-500' :
          'bg-red-500'
        }
      />
      
      {showDetails && (
        <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground mt-2">
          <div>Used: {formatBytes(storageData.usage)}</div>
          <div className="text-right">
            {storageData.quota ? 
              `Total: ${formatBytes(storageData.quota)}` : 
              `Available: ${formatBytes(storageData.remaining)}`
            }
          </div>
        </div>
      )}
    </Card>
  );
});

StorageHealthIndicator.displayName = 'StorageHealthIndicator';

export default StorageHealthIndicator;
